import { PayloadAction } from '@reduxjs/toolkit';
import {
  ConfigSaga,
  ConfigSelectors,
  ConfigTypes,
  DateUtils,
  EErrorLevel,
  HistogramActions,
  HistogramSelectors,
  ProgressSelectors,
  RemoteConfigService,
  StatsSelectors,
  getSessionFocusDomain,
  getWorkoutId,
  logger,
  preloadAgeGroupSaga,
  preloadStatsSaga,
  waitForFirebaseReadySaga,
  waitForFullProgressSaga,
} from '@web_opxp/features';
import { filter, find, findIndex, map, maxBy, orderBy, sortBy, union } from 'lodash';
import { all, call, delay, fork, put, select, take, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';
import { ApiService } from 'web_core_library';
import { ASSESSMENT_SESSION_ID, CYCLE_INTENSITY, DEFAULT_CATEGORY_ID, TRAINING_DOMAINS } from '../base/constants';
import ENV from '../base/env';
import RevenueModelConfig from '../base/revenue-model-config.json';
import TrainingConfig from '../base/training-config.json';
import {
  EDomain,
  EExerciseCategory,
  IAssessmentConfigOPXP,
  ITrainingIntensity,
  IWorkoutPopularityConfig,
} from '../base/types';
import { makeExerciseCategoryStatString } from '../base/utils';
import WorkoutPopularityConfig from '../base/workout-popularity-config.json';
import { SessionActions } from '../session';
import { CYCLES_LOAD_LIMIT } from './constants';
import CyclesDB, { clientDBArrayExpired, clientDBValueExpired, clientDBValueNeedsSync } from './cyclesDB';
import CyclesService from './cyclesService';
import * as Selectors from './selectors';
import { CyclesActions } from './slice';
import * as Types from './types';
import * as Utils from './utils';

export default function* cyclesWatcher() {
  yield all([
    takeEvery(CyclesActions.initCycles, initCyclesSaga),
    takeLatest(CyclesActions.loadCycles, loadCyclesSaga),
    takeEvery(CyclesActions.createAssessment, createAssessmentSaga),
    takeEvery(CyclesActions.finishCycle, finishCycleSaga),
    takeEvery(CyclesActions.saveCycle, saveCycleSaga),
    takeLatest(CyclesActions.syncCycles, syncDataWithBackendSaga),
    takeEvery(CyclesActions.completeWorkout, updateWorkoutStatusSaga),
    takeEvery(CyclesActions.skipWorkout, updateWorkoutStatusSaga),
    takeEvery(CyclesActions.replaceCurrentWorkout, replaceCurrentWorkoutSaga),
    takeEvery(CyclesActions.rebuildAssessment, rebuildAssessmentSaga),
    takeEvery(CyclesActions.createTraining, createTrainingSaga),
    takeEvery(CyclesActions.replaceWorkout, replaceWorkoutSaga),
    takeEvery(CyclesActions.finishCurrentSession, finishCurrentSessionSaga),
    takeEvery(CyclesActions.cloneCycle, cloneLastCycleSaga),
    takeEvery(CyclesActions.cloneLastSession, cloneLastSessionSaga),
    takeLeading(CyclesActions.cyclesClean, cleanCacheSaga),
    takeEvery(CyclesActions.loadAssessment, loadAssessmentSessionSaga),
  ]);
}

export function* initCyclesSaga(action: PayloadAction<Types.IInitCyclesPayload>) {
  const { userId } = action.payload;
  yield call(CyclesService.init, ApiService);
  yield call(CyclesDB.init, userId);
  yield put(CyclesActions.loadCycles());
}

export function* loadCyclesSaga() {
  const trainingStatus: Types.ITrainingDBStatus = yield call(loadCyclesStatisticSaga);
  const {
    currentCycleId,
    currentSessionId,
    currentCycleLength,
    finishedCycles,
    finishedSessions,
    numberWorkouts,
    firstTimestamp,
    lastTimestamp,
  } = trainingStatus;
  yield put(
    CyclesActions.updateStats(
      currentCycleId,
      currentSessionId,
      currentCycleLength,
      finishedCycles,
      finishedSessions,
      numberWorkouts,
      firstTimestamp,
      lastTimestamp
    )
  );
  // load current cycle data
  yield call(loadCurrentCycleSaga, currentCycleId);
  // load assessment data
  // BW-5328 - see what will happen if assessment
  // will not be loaded in advance
  // yield call(loadLastAssessmentSessionSaga);
  // detect where user is in cycle
  yield call(detectCurrentCycleStatus);
  yield call(forwardSessionIfNeeded);
  // accessability check
  yield call(validateCurrentSession);
  yield put(CyclesActions.cyclesReady());
}

export function* loadCyclesStatisticSaga() {
  let trainingStatus: Types.ITrainingDBStatus;
  try {
    const cachedStats: Types.ITrainingClientStatus | undefined = yield call(CyclesDB.getTrainingStats);
    if (!clientDBValueExpired(cachedStats) && cachedStats) {
      // if cached value not expired - use this value
      trainingStatus = {
        currentCycleId: cachedStats.currentCycleId,
        currentSessionId: cachedStats.currentSessionId,
        currentCycleLength: cachedStats.currentCycleLength,
        finishedCycles: cachedStats.finishedCycles,
        finishedSessions: cachedStats.finishedSessions,
        numberWorkouts: cachedStats.numberWorkouts,
        firstTimestamp: cachedStats.firstTimestamp,
        lastTimestamp: cachedStats.lastTimestamp,
      };
      return trainingStatus;
    }
  } catch (error) {
    // do nothing - just fallback to api call
    yield call(logger, error, EErrorLevel.Error);
  }
  // if no value found or value is expired - load from api
  try {
    const statisticResponse: Types.TTrainingStatisticsResponse = yield call(CyclesService.getUserStatistic);
    // ensure all values are set
    const {
      currentCycleId,
      currentCycleLastSessionId,
      currentCycleLength,
      finishedCycles,
      finishedSessions,
      numberWorkouts,
      firstTimestamp,
      lastTimestamp,
    } = statisticResponse.data.trainingstatistics;
    // override null values with fallbacks
    trainingStatus = {
      currentCycleId: currentCycleId || 1,
      currentSessionId: currentCycleLastSessionId ?? ASSESSMENT_SESSION_ID,
      currentCycleLength: currentCycleLength ?? 0,
      finishedCycles: finishedCycles ?? 0,
      finishedSessions: finishedSessions ?? 0,
      numberWorkouts: numberWorkouts ?? 0,
      firstTimestamp: firstTimestamp ?? 0,
      lastTimestamp: lastTimestamp ?? 0,
    };
  } catch (error) {
    yield call(logger, error, EErrorLevel.Error);
    // rethrow error further to handle it on the top level
    throw error;
  }
  // save stats to local db if available
  yield fork(saveTrainingStatsToClient, trainingStatus);
  return trainingStatus;
}

export function* saveTrainingStatsToClient(
  stats: Types.ITrainingDBStatus,
  syncTimestamp: number = DateUtils.getUnixTimestamp()
) {
  try {
    // save loaded value to
    const dbValue: Types.ITrainingClientStatus = {
      ...stats,
      syncTimestamp,
    };
    yield call(CyclesDB.saveTrainingStats, dbValue);
  } catch (error) {
    // fail silently if db is not available
    yield call(logger, error, EErrorLevel.Error);
  }
}

export function* loadLastAssessmentSessionSaga() {
  const currentCycleId = yield select(Selectors.getCurrentCycleId);
  if (currentCycleId < 2) {
    // if user has only one cycle its assessment will be loaded by default with current cycle data
    return;
  }
  // assessment for current cycle will be loaded by the normal cycle loading process
  let cycleId = currentCycleId - 1;
  let loaded = false;
  let cyclesLoaded = 0;
  do {
    yield call(loadCycleSessionSaga, cycleId, ASSESSMENT_SESSION_ID);
    // detect if session was loaded successfuly
    const session = yield select(Selectors.getCycleSession, cycleId, ASSESSMENT_SESSION_ID);
    loaded = !!session;
    cycleId -= 1;
    cyclesLoaded += 1;
    // See BW-5923. We limit amount of cycles loaded to prevent long waiting times.
  } while (cycleId > 0 && !loaded && cyclesLoaded < CYCLES_LOAD_LIMIT);
}

export function* loadAssessmentSessionSaga(action: PayloadAction<number>) {
  const cycleId = action.payload;
  yield call(loadCycleSessionSaga, cycleId, ASSESSMENT_SESSION_ID);
  // notifiy caller
  yield put(CyclesActions.loadAssessmentComplete(cycleId));
}

export function* loadCurrentCycleSaga(currentCycleId: number) {
  let startTimestamp = 0;
  let finishedTimestamp = 0;
  // load current cycle status from DB
  let cycleStatus: Types.ICycleClientStatus | undefined = yield call(loadCycleFromDB, currentCycleId);
  if (!cycleStatus || clientDBValueExpired(cycleStatus)) {
    // if cycle status is expired then probably session data is expired too because we now load them together
    const backendData: Types.ICycleStatus | undefined = yield call(loadCycleFromBackend, currentCycleId);
    if (!backendData) {
      return;
    }
    // update current cycle stats
    startTimestamp = backendData.startTimestamp;
    finishedTimestamp = backendData.finishedTimestamp;
    // save sessions to cache and state
    const sessions = Utils.convertSessionStatusFromApi(backendData);
    for (const sessionStatus of sessions) {
      // save session to local DB for further use
      yield call(saveSessionToDB, sessionStatus);
      // udpate state
      yield put(CyclesActions.updateSession(sessionStatus));
    }
    // convert workouts data structure for client use
    const workouts = Utils.convertWorkoutsStatusFromApi(backendData);
    // store workouts to cache and state
    yield call(saveWorkoutsToDB, workouts);
    yield put(CyclesActions.updateWorkouts(workouts));
    // store cycle status to cache
    cycleStatus = Utils.createCycleClientStatus(currentCycleId, finishedTimestamp, startTimestamp);
    yield call(saveCycleToDB, cycleStatus);
  } else {
    // update current cycle stats from loaded data
    startTimestamp = cycleStatus.startTimestamp;
    finishedTimestamp = cycleStatus.finishedTimestamp;
    // get sessions from cache
    const sessions: Types.ISessionClientStatus[] | undefined = yield call(loadCycleSessionsFromDB, currentCycleId);
    if (!sessions) {
      logger('No sessions data found in the training DB!', EErrorLevel.Warning);
      throw new Error('No sessions data found in the training DB');
    }
    for (const sessionStatus of sessions) {
      // udpate state
      yield put(CyclesActions.updateSession(sessionStatus));
      // load workouts for given session
      const workouts: Types.IWorkoutClientStatus[] | undefined = yield call(
        loadWorkoutsFromDB,
        currentCycleId,
        sessionStatus.sessionId
      );
      if (!workouts) {
        logger(
          `No workouts data found in the training DB for session ${sessionStatus.sessionId}!`,
          EErrorLevel.Warning
        );
        continue;
      }
      yield put(CyclesActions.updateWorkouts(workouts));
    }
  }
  yield put(CyclesActions.updateCycle(cycleStatus));
  yield put(CyclesActions.updateCycleStats(startTimestamp, finishedTimestamp));
}

export function* loadCycleFromDB(cycleId: number) {
  try {
    const cycle: Types.ICycleClientStatus | undefined = yield call(CyclesDB.getCycle, cycleId);
    return cycle;
  } catch (error) {
    // do nothing - just fallback to api call
    yield call(logger, error, EErrorLevel.Error);
  }
}

export function* saveCycleToDB(cycle: Types.ICycleClientStatus) {
  try {
    yield call(CyclesDB.saveCycle, cycle);
  } catch (error) {
    yield call(logger, error, EErrorLevel.Error);
  }
}

export function* loadCycleSessionSaga(cycleId: number, sessionId: number) {
  try {
    // load session from local DB
    let sessionStatus: Types.ISessionClientStatus | undefined = yield call(loadSessionFromDB, cycleId, sessionId);
    // load workouts for given session
    let workouts: Types.IWorkoutClientStatus[] | undefined = yield call(loadWorkoutsFromDB, cycleId, sessionId);
    // check if we need to load session from backend
    // if we did not saved something to backend we should not overwrite it with data from backend
    const sessionNeedsSync = sessionStatus && clientDBValueNeedsSync(sessionStatus);
    // if values in DB are not expired
    const sessionIsNotExpired = !clientDBValueExpired(sessionStatus) && !clientDBArrayExpired(workouts);
    // or if session was finished already
    const sessionFinished = sessionStatus && sessionStatus.finishedTimestamp > 0;
    // session is valid and not needed to be loaded from backend
    const sessionIsValid = sessionNeedsSync || sessionIsNotExpired || sessionFinished;
    // if need to be loaded - try to load from api
    if (!sessionIsValid) {
      const backendData: Types.ICycleStatus | undefined = yield call(loadSessionFromBackend, cycleId, sessionId);
      if (!backendData) {
        return;
      }
      const currentCycleId = yield select(Selectors.getCurrentCycleId);
      if (cycleId === currentCycleId) {
        // update current cycle stats in case they were not set yet
        yield put(CyclesActions.updateCycleStats(backendData.startTimestamp, backendData.finishedTimestamp));
      }
      // convert session data structure for client use
      sessionStatus = Utils.convertSessionStatusFromApi(backendData)[0];
      if (!sessionStatus) {
        // if no session was loaded - skip the rest
        return;
      }
      // save session to local DB for further use
      yield call(saveSessionToDB, sessionStatus);
      // convert workouts data structure for client use
      workouts = Utils.convertWorkoutsStatusFromApi(backendData);
      yield call(saveWorkoutsToDB, workouts);
    }
    if (sessionStatus) yield put(CyclesActions.updateSession(sessionStatus));
    yield put(CyclesActions.updateWorkouts(workouts || []));
  } catch (error) {
    yield call(logger, error, EErrorLevel.Error);
    throw error;
  }
}

export function* loadSessionFromDB(cycleId: number, sessionId: number) {
  try {
    const session: Types.ISessionClientStatus | undefined = yield call(CyclesDB.getSession, cycleId, sessionId);
    return session;
  } catch (error) {
    // do nothing - just fallback to api call
    yield call(logger, error, EErrorLevel.Error);
  }
}

export function* loadCycleSessionsFromDB(cycleId: number) {
  try {
    const sessions: Types.ISessionClientStatus[] | undefined = yield call(CyclesDB.getAllSessionsForCycle, cycleId);
    return sessions;
  } catch (error) {
    // do nothing - just fallback to api call
    yield call(logger, error, EErrorLevel.Error);
  }
}

export function* saveSessionToDB(session: Types.ISessionClientStatus) {
  try {
    yield call(CyclesDB.saveSession, session);
  } catch (error) {
    yield call(logger, error, EErrorLevel.Error);
  }
}

export function* loadWorkoutsFromDB(cycleId: number, sessionId: number) {
  try {
    const workouts: Types.IWorkoutClientStatus[] | undefined = yield call(CyclesDB.getWorkouts, cycleId, sessionId);
    return workouts;
  } catch (error) {
    // do nothing - just fallback to api call
    yield call(logger, error, EErrorLevel.Error);
  }
}

export function* saveWorkoutsToDB(workouts: Types.IWorkoutClientStatus[]) {
  try {
    yield call(CyclesDB.saveWorkouts, workouts);
  } catch (error) {
    yield call(logger, error, EErrorLevel.Error);
  }
}

export function* loadCycleFromBackend(cycleId: number) {
  try {
    const apiResponse: Types.TCycleStatusResponse = yield call(CyclesService.getCycle, cycleId);
    const cycles = apiResponse.data.trainingcycles;
    return cycles[0];
  } catch (error) {
    yield call(logger, error, EErrorLevel.Error);
    // throw error further to show user some message
    throw error;
  }
}

export function* loadSessionFromBackend(cycleId: number, sessionId: number) {
  try {
    const apiResponse: Types.TCycleStatusResponse = yield call(CyclesService.getCycleSession, cycleId, sessionId);
    const cycles = apiResponse.data.trainingcycles;
    return cycles[0];
  } catch (error) {
    yield call(logger, error, EErrorLevel.Error);
    // throw error further to show user some message
    throw error;
  }
}

export function* detectCurrentCycleStatus() {
  const finishedTimestamp = yield select(Selectors.getCycleFinishedTimestamp);
  if (finishedTimestamp > 0) {
    // cycle is obvously completed
    yield put(CyclesActions.updateCycleStatus(Types.ECycleStatus.CycleCompleted));
    return;
  }
  const sessionId = yield select(Selectors.getCurrentSessionId);
  const sessionFinished = yield select(Selectors.isCurrentSessionFinished);
  if (sessionId === ASSESSMENT_SESSION_ID) {
    // current session is assessment
    if (sessionFinished) {
      // following sessions created?
      const cycleLength: number = yield select(Selectors.getCycleLength);
      if (cycleLength === 0) {
        // assessment is finished
        yield put(CyclesActions.updateCycleStatus(Types.ECycleStatus.AssessmentFinished));
        return;
      } else {
        // assessment finished but first session is not started yet
        yield put(CyclesActions.updateSessionId(1));
        yield put(CyclesActions.updateCycleStatus(Types.ECycleStatus.SessionRunning));
        return;
      }
    }
    // assessment not finished = running
    yield put(CyclesActions.updateCycleStatus(Types.ECycleStatus.AssessmentRunning));
    return;
  }
  if (sessionFinished) {
    const cycleLength = yield select(Selectors.getCycleLength);
    if (sessionId === cycleLength) {
      yield put(CyclesActions.updateCycleStatus(Types.ECycleStatus.CycleCompleted));
      return;
    }
    // session finished
    yield put(CyclesActions.updateCycleStatus(Types.ECycleStatus.SessionFinished));
    return;
  }
  // session is running
  yield put(CyclesActions.updateCycleStatus(Types.ECycleStatus.SessionRunning));
}

export function* forwardSessionIfNeeded() {
  let status: Types.ECycleStatus = yield select(Selectors.getCycleStatus);
  // session finished view is not supported by dashboard
  while (status === Types.ECycleStatus.SessionFinished) {
    const sessionId = yield select(Selectors.getCurrentSessionId);
    const cycleLength = yield select(Selectors.getCycleLength);
    if (sessionId > cycleLength) {
      // so we can see if this happens in our logs
      throw new Types.CyclesError('Data error. Cycle data is inconsistent!');
    }
    if (sessionId === cycleLength) {
      // the last session of the cycle - nowhere to push
      return;
    }
    yield put(CyclesActions.updateSessionId(sessionId + 1));
    yield call(detectCurrentCycleStatus);
    status = yield select(Selectors.getCycleStatus);
  }
}

export function* createAssessmentSaga(action: PayloadAction<Types.ICreateAssessmentPayload>) {
  const { config, addCFQ } = action.payload;
  // check if assessment was already started
  const assessmentRunning = yield select(Selectors.isAssessmentRunning);
  if (assessmentRunning) {
    yield put(CyclesActions.assessmentCreated());
    return;
  }
  const currentCycleId = yield select(Selectors.getCurrentCycleId);
  // finish current cycle if not finished
  yield put(CyclesActions.finishCycle());
  yield take(CyclesActions.cycleFinished);
  // create new cycle with assessment session
  const nextCycleId = currentCycleId + 1;
  const nextCycle = Utils.createCycleWithAssessment(nextCycleId, config, addCFQ);
  yield call(createCycleSaga, nextCycle);
  // check update state for current cycle
  yield call(detectCurrentCycleStatus);
  yield put(CyclesActions.assessmentCreated());
}

export function* finishCycleSaga() {
  const cycleId = yield select(Selectors.getCurrentCycleId);
  // check if current cycle marked as finished or not
  const isCycleFinished = yield select(Selectors.isCycleCompleted);
  if (!isCycleFinished) {
    // if cycle not finished - some wirkouts/sessions are still active
    let sessionId = yield select(Selectors.getCurrentSessionId);
    const cycleLength = yield select(Selectors.getCycleLength);
    while (sessionId <= cycleLength) {
      const sessionFinished = yield select(Selectors.isCurrentCycleSessionFinished, sessionId);
      if (!sessionFinished) {
        yield call(finishSessionSaga, cycleId, sessionId);
      }
      sessionId++;
    }
  }
  // we need to update cycle data to "finished" and save it
  const startTimestamp = yield select(Selectors.getCycleStartTimestamp);
  // update finished timestamp
  yield put(CyclesActions.updateCycleStats(startTimestamp, -1));
  // start saving cycle data
  yield put(CyclesActions.saveCycle());
  yield take(CyclesActions.cycleSaved);
  yield put(CyclesActions.cycleFinished());
}

export function* saveCycleSaga() {
  // compile cycle data
  const cycleId = yield select(Selectors.getCurrentCycleId);
  const finishedTimestamp = yield select(Selectors.getCycleFinishedTimestamp);
  const startTimestamp = yield select(Selectors.getCycleStartTimestamp);
  // mark cycle as unsync for saving
  const cycle = Utils.createCycleClientStatus(cycleId, finishedTimestamp, startTimestamp, 0);
  // update cycle in state
  yield put(CyclesActions.updateCycle(cycle));
  // save to local DB
  yield call(saveCycleToDB, cycle);
  // initiate saving to backend
  yield put(CyclesActions.syncCycles());
  // check state for current cycle for changes
  yield call(detectCurrentCycleStatus);
  yield put(CyclesActions.cycleSaved());
}

export function* syncDataWithBackendSaga() {
  try {
    // collect all cycles/sessions to sync from state
    const toSave: Types.ICycleStatus[] = yield select(Selectors.getCyclesToSync);
    // call api to save cycles
    yield call(CyclesService.updateCycles, toSave);
    // mark saved data as synced
    yield call(markSavedDataAsSynchronized, toSave);
  } catch (error) {
    yield call(logger, error, EErrorLevel.Error);
  }
}

function pushMarkedWorkouts(workouts: Types.IWorkoutStatus[], cachedWorkouts: Types.IWorkoutClientStatus[]) {
  const workoutsToMark: Types.IWorkoutClientStatus[] = [];
  for (const workout of workouts) {
    const workoutToMark = cachedWorkouts.find((w) => w.workoutId === workout.workoutId && w.index === workout.index);
    if (workoutToMark) {
      workoutsToMark.push(Utils.markAsSync(workoutToMark));
    }
  }
  return workoutsToMark;
}

export function* markSavedDataAsSynchronized(saved: Types.ICycleStatus[]) {
  for (const cycle of saved) {
    const cycleId = cycle.cycleId;
    const cachedCycle: Types.ICycleClientStatus | undefined = yield call(loadCycleFromDB, cycleId);
    if (!cachedCycle) {
      // if cache is empty - something went wrong and we should not touch it
      continue;
    }
    const syncCycle = Utils.markAsSync(cachedCycle);
    yield call(saveCycleToDB, syncCycle);
    yield put(CyclesActions.updateCycle(syncCycle));
    const cycleSessions = cycle.sessions;
    for (const session of cycleSessions) {
      const sessionId = session.sessionId;
      const cachedSession: Types.ISessionClientStatus | undefined = yield call(loadSessionFromDB, cycleId, sessionId);
      const cachedWorkouts: Types.IWorkoutClientStatus[] | undefined = yield call(
        loadWorkoutsFromDB,
        cycleId,
        sessionId
      );
      if (!cachedSession || !cachedWorkouts) {
        continue;
      }
      const syncSession = Utils.markAsSync(cachedSession);
      yield call(saveSessionToDB, syncSession);
      yield put(CyclesActions.updateSession(syncSession));
      const workoutsToMark: Types.IWorkoutClientStatus[] = pushMarkedWorkouts(session.workouts, cachedWorkouts);
      yield call(saveWorkoutsToDB, workoutsToMark);
      yield put(CyclesActions.updateWorkouts(workoutsToMark));
    }
  }
}

export function* createCycleSaga(cycle: Types.ICycleStatus) {
  try {
    yield call(CyclesService.createCycle, cycle);
    const stats: Types.ITrainingDBStatus = yield select(Selectors.getCurrentCycleStats);
    const cycleLength = cycle.sessions.filter((session) => session.sessionId !== ASSESSMENT_SESSION_ID).length;
    // udpate stats in state
    yield put(
      CyclesActions.updateStats(
        cycle.cycleId,
        ASSESSMENT_SESSION_ID,
        cycleLength,
        stats.finishedCycles + 1,
        stats.finishedSessions,
        stats.numberWorkouts,
        stats.firstTimestamp,
        stats.lastTimestamp
      )
    );
    yield call(saveStateStatsToDB);
    const cycleStatus = Utils.createCycleClientStatus(cycle.cycleId, 0, cycle.startTimestamp);
    yield call(saveCycleToDB, cycleStatus);
    // update cycle stats
    yield put(CyclesActions.updateCycleStats(cycleStatus.startTimestamp, 0));
    // convert session data structure for client use
    const sessionStatuses = Utils.convertSessionStatusFromApi(cycle);
    for (const sessionStatus of sessionStatuses) {
      // save session to local DB for further use
      yield call(saveSessionToDB, sessionStatus);
      yield put(CyclesActions.updateSession(sessionStatus));
    }
    // convert workouts data structure for client use
    const workouts = Utils.convertWorkoutsStatusFromApi(cycle);
    yield call(saveWorkoutsToDB, workouts);
    yield put(CyclesActions.updateWorkouts(workouts));
  } catch (error: any) {
    yield call(logger, error, EErrorLevel.Error);
    throw new Types.CyclesError(`New cycle creation failed! ${error.message}`);
  }
}

export function* waitIfLoading() {
  const isLoading = yield select(Selectors.isCyclesLoading);
  if (isLoading) {
    // wait if data is still loading after saving last state
    yield take(CyclesActions.cyclesReady);
  }
}

export function* updateWorkoutStatusSaga(action: PayloadAction<Types.ICompleteWorkoutPayload>) {
  const { workoutIndex, newStatus } = action.payload;
  yield call(waitIfLoading);
  const currentWorkoutStatus: Types.IWorkoutClientStatus = yield select(
    Selectors.getCurrentSessionWorkoutByRelativeIndex,
    workoutIndex
  );
  const workoutCompleted = newStatus === Types.ETrainingStatus.Finished;
  const workoutSkipped = newStatus === Types.ETrainingStatus.Skipped;
  const isFinished = workoutCompleted || workoutSkipped;
  const newWorkoutStatus: Types.IWorkoutClientStatus = {
    ...currentWorkoutStatus,
    status: newStatus,
    finishedTimestamp: isFinished ? -1 : currentWorkoutStatus.finishedTimestamp,
    syncTimestamp: 0,
  };
  // update status in state
  yield call(saveWorkoutsToDB, [newWorkoutStatus]);
  yield put(CyclesActions.updateWorkouts([newWorkoutStatus]));
  const isAssessment = newWorkoutStatus.sessionId === ASSESSMENT_SESSION_ID;
  // workout was already finished before
  const workoutRepeat = currentWorkoutStatus.status === Types.ETrainingStatus.Finished;
  if (workoutCompleted && !workoutRepeat && !isAssessment) {
    // increase completed workouts counter if workout was completed
    // but only for non-assessment workouts
    // and ignore repeated workouts
    yield call(increaseWorkoutsCounter, isFinished ? DateUtils.getUnixTimestamp() : undefined);
    yield call(saveStateStatsToDB);
  }
  // update possible change in session/cycle status
  yield call(updateCurrentSessionStatusSaga);
  // start saving
  yield put(CyclesActions.saveCycle());
  // report that operation is done
  yield put(CyclesActions.updateWorkoutStatusDone());
}

export function* updateCurrentSessionStatusSaga() {
  const workouts: Types.IWorkoutClientStatus[] = yield select(Selectors.getCurrentSessionWorkouts);
  const sessionFinished: boolean = yield call(Utils.allWorkoutsAreFinished, workouts);
  if (!sessionFinished) {
    return;
  }
  const currentSessionStatus: Types.ISessionClientStatus | undefined = yield select(Selectors.getCurrentSession);
  if (!currentSessionStatus || currentSessionStatus.status === Types.ETrainingStatus.Finished) {
    return;
  }
  const newSessionStatus: Types.ISessionClientStatus = {
    ...currentSessionStatus,
    status: Types.ETrainingStatus.Finished,
    finishedTimestamp: -1,
    syncTimestamp: 0,
  };
  yield call(saveSessionToDB, newSessionStatus);
  yield put(CyclesActions.updateSession(newSessionStatus));
  // increase completed sessions counter
  const isAssessment = newSessionStatus.sessionId === ASSESSMENT_SESSION_ID;
  if (!isAssessment) {
    yield call(increaseSessionCounter);
  }
  // if session finished we should invalidate training status
  yield call(saveStateStatsToDB);
}

export function* replaceCurrentWorkoutSaga(action: PayloadAction<Types.IReplaceCurrentWorkoutPayload>) {
  const { workoutId, workoutIndex } = action.payload;
  yield call(waitIfLoading);
  const workouts: Types.IWorkoutClientStatus[] = yield select(Selectors.getCurrentSessionWorkouts);
  const updatedWorkouts: Types.IWorkoutClientStatus[] = yield call(
    Utils.replaceCurrentWorkout,
    workouts,
    workoutId,
    workoutIndex
  );
  yield call(saveWorkoutsToDB, updatedWorkouts);
  yield put(CyclesActions.updateWorkouts(updatedWorkouts));
  yield put(CyclesActions.saveCycle());
  yield put(SessionActions.loadSession());
}

export function* rebuildAssessmentSaga(action: PayloadAction<IAssessmentConfigOPXP>) {
  const config = action.payload;
  yield call(waitIfLoading);
  const currentCycleId = yield select(Selectors.getCurrentCycleId);
  // get existing workouts
  const workouts: Types.IWorkoutClientStatus[] = yield select(Selectors.getCurrentSessionWorkouts);
  // rewrite the session workouts
  const updatedWorkouts: Types.IWorkoutClientStatus[] = yield call(
    Utils.rewriteCycleAssessment,
    currentCycleId,
    workouts,
    config
  );
  yield call(saveWorkoutsToDB, updatedWorkouts);
  yield put(CyclesActions.updateWorkouts(updatedWorkouts));
  // update possible change in session/cycle status
  yield call(updateCurrentSessionStatusSaga);
  // save changes
  yield put(CyclesActions.saveCycle());
  // wait for success
  yield take(CyclesActions.cycleSaved);
  // check/update state for current cycle
  yield call(detectCurrentCycleStatus);
  yield put(CyclesActions.assessmentCreated());
}

export function* createTrainingSaga(action: PayloadAction<number | undefined>) {
  const addSessions = action.payload;
  try {
    yield call(waitIfLoading);
    const cycleId = yield select(Selectors.getCurrentCycleId);
    yield call(recreateSessions, addSessions);
    const stats: Types.ITrainingDBStatus = yield select(Selectors.getCurrentCycleStats);
    const cycleLength = yield select(Selectors.getAmountOfSessionsInCurrentCycle);
    const assessmentSession = yield select(Selectors.getCycleSession, cycleId, ASSESSMENT_SESSION_ID);
    const nextSessionId = yield select(Selectors.getNextSessionId);
    const updatedStats: Types.ITrainingDBStatus = {
      currentCycleId: cycleId,
      currentCycleLength: assessmentSession ? cycleLength - 1 : cycleLength, // ignore assessment session if present
      currentSessionId: nextSessionId,
      // these fields are not changing
      finishedCycles: stats.finishedCycles,
      finishedSessions: stats.finishedSessions,
      numberWorkouts: stats.numberWorkouts,
      firstTimestamp: stats.firstTimestamp,
      lastTimestamp: stats.lastTimestamp,
    };
    // update data in state
    yield put(
      CyclesActions.updateStats(
        updatedStats.currentCycleId,
        updatedStats.currentSessionId,
        updatedStats.currentCycleLength,
        updatedStats.finishedCycles,
        updatedStats.finishedSessions,
        updatedStats.numberWorkouts,
        updatedStats.firstTimestamp,
        updatedStats.lastTimestamp
      )
    );
    yield call(saveTrainingStatsToClient, updatedStats);
    const startTimestamp = yield select(Selectors.getCycleStartTimestamp);
    const cycleStatus = Utils.createCycleClientStatus(cycleId, 0, startTimestamp, 1);
    yield call(saveCycleToDB, cycleStatus);
    // save everything to backend
    yield put(CyclesActions.saveCycle());
    // wait for success
    yield take(CyclesActions.cycleSaved);
    yield put(CyclesActions.trainingCreated());
  } catch (error: any) {
    yield call(logger, [`Cannot create training: ${error.message}`, error], EErrorLevel.Error);
    yield put(CyclesActions.createTrainingFailed());
  }
}

export function* waitForWorkoutHistogram(workoutId: number) {
  let loaded = false;
  while (!loaded) {
    loaded = yield select(HistogramSelectors.isWorkoutHistogramLoaded, workoutId);
    if (!loaded) {
      yield delay(300);
    }
  }
}

export function* preloadWorkoutHistograms(workoutIds: number[], ageGroupId: number) {
  const promiseBuffer: Array<Promise<unknown>> = [];
  for (const workoutId of workoutIds) {
    const task = yield fork(waitForWorkoutHistogram, workoutId);
    const promise = task.toPromise();
    promiseBuffer.push(promise);
  }
  // start loading histograms
  yield put(HistogramActions.loadHistogram(workoutIds, ageGroupId));
  // wait until all workout histograms will be loaded
  yield Promise.all(promiseBuffer);
}

export function* recreateSessions(addSessions = 0) {
  const cycleFinished = yield select(Selectors.isCycleCompleted);
  const assessmentFinished = yield select(Selectors.isAssessmentFinished);
  if (!assessmentFinished || (cycleFinished && addSessions === 0)) {
    // Assessment for that cycle is ongoing or cycle is already finished. Cannot recreate sessions
    yield call(
      logger,
      'Assessment for that cycle is ongoing or cycle is already finished. Cannot recreate sessions!',
      EErrorLevel.Warning
    );
    return;
  }
  const cycleId = yield select(Selectors.getCurrentCycleId);
  // 1. GATHER DATA
  // get workouts configuration
  yield call(ConfigSaga.preloadConfig);
  yield call(preloadStatsSaga);
  const categoryGroupIds = yield select(ConfigSelectors.getAllSupportedExercisesGroupIds);
  const categoryFilteredWorkouts: ConfigTypes.IWorkoutConfig[] = [];
  const allExcludedWorkoutsIds: number[] = [];
  for (const categoryGroupId of categoryGroupIds) {
    const categoryIdStat: EExerciseCategory | undefined = yield select(
      StatsSelectors.getStatIntValueById,
      makeExerciseCategoryStatString(categoryGroupId)
    );
    // if stat is not set use default category (normal difficulty)
    const categoryId = categoryIdStat ?? DEFAULT_CATEGORY_ID;
    const workoutId = getWorkoutId(categoryId, categoryGroupId);
    const isEnabled = yield select(ConfigSelectors.isWorkoutReleasedForWeb, workoutId);
    if (!isEnabled) {
      // skip exercise if its not released for web platform
      continue;
    }
    const workoutConfig: ConfigTypes.IWorkoutConfig = yield select(ConfigSelectors.getWorkoutConfig, workoutId);
    const workoutDisabled = yield select(StatsSelectors.getStatBooleanValueById, `9-4-${workoutId}`);
    if (!workoutDisabled) {
      // add workout to available configs only if its not disabled by user (see BW-4044)
      categoryFilteredWorkouts.push(workoutConfig);
    } else {
      allExcludedWorkoutsIds.push(workoutId);
    }
  }
  // calculate how much sessions we need to generate
  const sessionIdsToGenerate: number[] = [];
  const missingWorkoutBySessionId: Map<number, number> = new Map();
  let absoluteSessionIdFactor = 0;
  const absoluteFinishedSessionNumber: number = yield select(Selectors.getNumberOfFinishedSessions);
  // define locked workouts if user is not premium
  yield call(waitForFirebaseReadySaga);
  let lockedWorkoutGroupIds: number[] = [];
  const userIsPremium = yield select(Selectors.isUserPremium);
  if (!userIsPremium) {
    const revenueConfig: typeof RevenueModelConfig = yield call(
      RemoteConfigService.getObjectConfigValue,
      ENV.RemoteConfigRevenueModel,
      RevenueModelConfig
    );
    lockedWorkoutGroupIds = revenueConfig.premium_workouts_group_ids_without_advertisements;
  }
  // Get cycle intensity
  // Check if there is custom intensity for given cycle
  let cycleIntensity: ITrainingIntensity | undefined = yield call(Utils.getCustomCycleIntensity, cycleId);
  if (!cycleIntensity) {
    const selectedIntensity: number = yield select(StatsSelectors.getStatIntValueById, CYCLE_INTENSITY);
    cycleIntensity = yield call(Utils.getSelectedIntensity, selectedIntensity);
  }
  const currentAmountOfSessions = yield select(Selectors.getAmountOfSessionsInCurrentCycle);
  // if cycle already has sessions - add to them, otherwise generate amount of sessions defined in intensity config
  const sessionsInCycle = cycleIntensity ? cycleIntensity.session_amount_in_cycle : 0;
  const numberOfSessionsInCycle =
    currentAmountOfSessions > 1 ? currentAmountOfSessions + addSessions : sessionsInCycle + 1;
  const cycleCurrentSessions: Types.ISessionClientStatus[] = yield select(Selectors.getCurrentCycleSessions);
  const cycleNewSessions: Types.ISessionClientStatus[] = [];
  // 2. KEEP ALREADY FINISHED AND ONGOING SESSIONS
  // Store already done exercises for further workout selection to avoid many duplicates.
  // We also need to keep already finished and ongoing sessions.
  let workoutsToGenerate = 0;
  let highestFinishedSessionId = 0;
  for (const session of cycleCurrentSessions) {
    const { cycleId, sessionId } = session;
    const isOngoing = yield select(Selectors.isSessionOngoing, cycleId, sessionId);
    const isFinished = Utils.isSessionFinished(session);
    const isSkipped = Utils.isSessionSkipped(session);
    // copy already finished or started sessions
    if (isOngoing || isFinished || isSkipped) {
      cycleNewSessions.push(session);
      if (isOngoing) {
        const sessionWorkouts: Types.IWorkoutClientStatus[] = yield select(
          Selectors.getSessionWorkouts,
          session.cycleId,
          session.sessionId
        );
        // Add all already done exercises
        for (const workout of sessionWorkouts) {
          if (
            session.sessionId !== ASSESSMENT_SESSION_ID &&
            !(
              workout.status === Types.ETrainingStatus.Finished ||
              workout.status === Types.ETrainingStatus.Ongoing ||
              workout.status === Types.ETrainingStatus.Skipped
            )
          ) {
            workoutsToGenerate++;
          }
        }
        missingWorkoutBySessionId.set(session.sessionId, workoutsToGenerate);
        sessionIdsToGenerate.push(session.sessionId);
      } else {
        highestFinishedSessionId = Math.max(highestFinishedSessionId, session.sessionId);
        absoluteSessionIdFactor = absoluteFinishedSessionNumber - highestFinishedSessionId;
      }
    }
  }
  // 3. CALCULATE DOMAIN DISTRIBUTION FOR CYCLE AND REDISTRIBUTE REMAINING WORKOUTS IN CYCLE
  // Calculate sessions amount by domain to know how many sessions should focus on what domain.
  const numberOfMissingSessions = Math.max(numberOfSessionsInCycle - cycleNewSessions.length, 0);
  const domainWeights: ConfigTypes.TWeightsByDomainMap = yield select(ConfigSelectors.getDomainWeights);
  // Get current doman settings and normalise them because user might have changed those settings
  let domainSettingsSum = 0;
  let normalizedDomainSettings: IDomainSetting[] = [];
  for (const domainId of TRAINING_DOMAINS) {
    const statId = `9-5-${domainId}`;
    const setting: number = yield select(StatsSelectors.getStatIntValueById, statId) ?? 0;
    normalizedDomainSettings.push({ domainId, setting });
    domainSettingsSum += setting;
  }
  normalizedDomainSettings = map(normalizedDomainSettings, (setting) => ({
    domainId: setting.domainId,
    setting: setting.setting / domainSettingsSum,
  }));
  const allWorkouts = categoryFilteredWorkouts;
  const allWorkoutIds = map(allWorkouts, 'workoutId');
  // Get histograms for workouts for users age group
  yield call(preloadAgeGroupSaga);
  const ageGroupId = yield select(StatsSelectors.getAgeGroup);
  yield call(preloadWorkoutHistograms, allWorkoutIds, ageGroupId);
  // Get Last Results for workouts
  yield call(waitForFullProgressSaga);
  const performanceByWorkout: Map<number, number> = new Map();
  for (const workoutId of allWorkoutIds) {
    const score: number = yield select(ProgressSelectors.getLastWorkoutScore, workoutId);
    const performance: number = yield select(HistogramSelectors.getWorkoutComparison, workoutId, score);
    performanceByWorkout.set(workoutId, performance);
  }
  // Create dictionary for domain weights by domainId and workoutId
  const domainWeightsForAllWorkouts: Map<number, Map<number, number>> = new Map();
  let workoutDomainWeights: ConfigTypes.IWorkoutDomainWeight[];
  for (const domainId of TRAINING_DOMAINS) {
    workoutDomainWeights = domainWeights.get(domainId) || [];
    const workoutsWeights: Map<number, number> = new Map();
    for (const workoutConfig of categoryFilteredWorkouts) {
      const workoutId = workoutConfig.workoutId;
      if (!workoutsWeights.has(workoutId)) {
        let weight = 0;
        for (const domainWeight of workoutDomainWeights) {
          if (domainWeight.workoutId === workoutId) {
            weight += domainWeight.weight / 100;
            break;
          }
        }
        workoutsWeights.set(workoutId, weight);
      }
    }
    domainWeightsForAllWorkouts.set(domainId, workoutsWeights);
  }
  // Get number of missing workouts to Generate
  let numberOfMissingWorkoutsInSession: number;
  let sessionIndex = cycleNewSessions.length > 0 ? cycleNewSessions[cycleNewSessions.length - 1].sessionId + 1 : 1;
  for (let index = 0; index < numberOfMissingSessions; index++) {
    // If that session already exists keep the number of workouts
    const sessionLength = yield select(Selectors.getSessionLength, cycleId, sessionIndex);
    const workoutsInSession = cycleIntensity ? cycleIntensity.workout_amount_in_session : 0;
    numberOfMissingWorkoutsInSession = currentAmountOfSessions > sessionIndex ? sessionLength : workoutsInSession;
    workoutsToGenerate += numberOfMissingWorkoutsInSession;
    if (!missingWorkoutBySessionId.has(sessionIndex)) {
      missingWorkoutBySessionId.set(sessionIndex, numberOfMissingWorkoutsInSession);
    }
    if (!sessionIdsToGenerate.includes(sessionIndex)) {
      sessionIdsToGenerate.push(sessionIndex);
    }
    sessionIndex++;
  }
  const popularityConfig: IWorkoutPopularityConfig = yield call(
    RemoteConfigService.getObjectConfigValue,
    ENV.RemoteConfigWorkoutPopularity,
    WorkoutPopularityConfig
  );
  const availableWorkoutsForCycle: TAvailableWorkoutsForSessions = yield call(
    getPopularWorkoutsForSessions,
    sessionIdsToGenerate,
    absoluteSessionIdFactor,
    categoryFilteredWorkouts,
    allExcludedWorkoutsIds,
    popularityConfig
  );
  const trainingConfig = yield call(RemoteConfigService.getObjectConfigValue, ENV.RemoteConfigTraining, TrainingConfig);
  if (!trainingConfig) {
    throw new Error('Assessment configuration is unavailable!');
  }
  const repeatFactor: number = trainingConfig.workout_repeat_factor;
  // Get distribution for every availibility group
  const sessionGroupDistributedWorkouts: Map<number[], Types.IWorkoutDistribution[]> = new Map();
  for (const availableWorkoutsForCycleEntry of availableWorkoutsForCycle) {
    // Calculate target distribution for available workouts based on cycle domain focus
    const availableWorkoutsIds = availableWorkoutsForCycleEntry.workoutIds;
    let availableWorkoutsWithTargetDistribution: Types.IWorkoutDistribution[] = [];
    let sumTargetDistribution = 0;
    let targetDistribution: number;
    for (const availableWorkoutId of availableWorkoutsIds) {
      targetDistribution = getTargetWorkoutDistribution(
        availableWorkoutId,
        lockedWorkoutGroupIds,
        normalizedDomainSettings,
        domainWeightsForAllWorkouts,
        categoryFilteredWorkouts
      );
      availableWorkoutsWithTargetDistribution.push({
        workoutId: availableWorkoutId,
        targetDistribution,
        amount: 0,
      });
      sumTargetDistribution += targetDistribution;
    }
    // security when sumTargetDistribution == 0 to be able to divide by it
    sumTargetDistribution = Math.max(sumTargetDistribution, 0.0001);
    // normalize target distribiution
    availableWorkoutsWithTargetDistribution = map(availableWorkoutsWithTargetDistribution, (d) => ({
      workoutId: d.workoutId,
      targetDistribution: d.targetDistribution / sumTargetDistribution,
      amount: 0,
    }));
    // Choose remaining workouts for the entire cycle base on domain distribution
    let missingWorkoutsForGroup = 0;
    for (const sessionId of availableWorkoutsForCycleEntry.sessionIds) {
      missingWorkoutsForGroup += missingWorkoutBySessionId.get(sessionId) ?? 0;
    }
    const groupDistributeWorkouts = distributeWorkouts(
      availableWorkoutsWithTargetDistribution,
      missingWorkoutsForGroup,
      repeatFactor
    );
    if (!sessionGroupDistributedWorkouts.has(availableWorkoutsForCycleEntry.sessionIds)) {
      sessionGroupDistributedWorkouts.set(availableWorkoutsForCycleEntry.sessionIds, groupDistributeWorkouts);
    }
  }
  // 4. UPDATE POSSIBLE ONGOING SESSION
  // Now update last session workouts in case it has any that are not finished or not ongoing
  let sessionNewWorkouts: Types.IWorkoutClientStatus[] = [];
  let numberOfMissingWorkouts = 0;
  const revenueConfig: typeof RevenueModelConfig = yield call(
    RemoteConfigService.getObjectConfigValue,
    ENV.RemoteConfigRevenueModel,
    RevenueModelConfig
  );
  const premiumWorkoutsInSessionPercentage: number = revenueConfig.percentage_of_premium_workouts_in_session;
  let numberOfLockedWorkouts = 0;
  let groupDistribution: Types.IWorkoutDistribution[] | undefined;
  let workoutIndex = 0;
  let currentSession = cycleNewSessions[cycleNewSessions.length - 1];
  if (currentSession) {
    const currentSessionWorkouts: Types.IWorkoutClientStatus[] = yield select(
      Selectors.getSessionWorkouts,
      currentSession.cycleId,
      currentSession.sessionId
    );
    for (const workout of currentSessionWorkouts) {
      // Copy already finished or started workouts
      if (
        workout.status === Types.ETrainingStatus.Finished ||
        workout.status === Types.ETrainingStatus.Ongoing ||
        workout.status === Types.ETrainingStatus.Skipped
      ) {
        sessionNewWorkouts.push(workout);
      }
    }
    workoutIndex = sessionNewWorkouts.length > 0 ? sessionNewWorkouts[sessionNewWorkouts.length - 1].index + 1 : 0;
    numberOfMissingWorkouts = Math.max(currentSessionWorkouts.length - sessionNewWorkouts.length, 0);

    if (numberOfMissingWorkouts > 0) {
      // Distribute workouts for session
      groupDistribution = findGroupDistribution(sessionGroupDistributedWorkouts, currentSession.sessionId);
      const distributionLength = groupDistribution ? groupDistribution.length : 0;
      const uniqueSessionExerciseAmount = getUniqueSessionExerciseAmount(
        numberOfMissingWorkouts,
        repeatFactor,
        distributionLength
      );
      const distributedSessionWorkouts = getWorkoutsForSession(
        currentSession.cycleId,
        currentSession.sessionId,
        groupDistribution,
        numberOfMissingWorkouts,
        uniqueSessionExerciseAmount,
        workoutIndex,
        repeatFactor
      );
      sessionNewWorkouts.push(...distributedSessionWorkouts);
      // Add potential locked workouts
      yield call(
        addLockedWorkouts,
        currentSession.cycleId,
        currentSession.sessionId,
        sessionNewWorkouts,
        lockedWorkoutGroupIds,
        numberOfLockedWorkouts,
        categoryFilteredWorkouts
      );
      // Order workouts in session
      sessionNewWorkouts = orderWorkoutsInSession(
        sessionNewWorkouts,
        performanceByWorkout,
        lockedWorkoutGroupIds,
        categoryFilteredWorkouts
      );
    }
    // store current session
    yield call(storeSession, currentSession, sessionNewWorkouts);
  }
  // 5. GENERATE REST OF REQUIRED SESSIONS
  sessionIndex = cycleNewSessions.length > 0 ? cycleNewSessions[cycleNewSessions.length - 1].sessionId + 1 : 1;

  for (let i = 0; i < numberOfMissingSessions; i++) {
    sessionNewWorkouts = [];
    // If that session already exists keep the number of workouts
    const sessionLength = yield select(Selectors.getSessionLength, cycleId, sessionIndex);
    numberOfMissingWorkouts =
      currentAmountOfSessions > sessionIndex ? sessionLength : (cycleIntensity?.workout_amount_in_session ?? 0);
    numberOfLockedWorkouts =
      lockedWorkoutGroupIds.length > 0
        ? Math.floor((numberOfMissingWorkouts * premiumWorkoutsInSessionPercentage) / 100)
        : 0;

    // Distribute workouts for session
    groupDistribution = findGroupDistribution(sessionGroupDistributedWorkouts, sessionIndex);
    const distributionLength = groupDistribution ? groupDistribution.length : 0;
    const uniqueSessionExerciseAmount = getUniqueSessionExerciseAmount(
      numberOfMissingWorkouts,
      repeatFactor,
      distributionLength
    );
    const distributedSessionWorkouts = getWorkoutsForSession(
      cycleId,
      sessionIndex,
      groupDistribution,
      numberOfMissingWorkouts,
      uniqueSessionExerciseAmount,
      workoutIndex,
      repeatFactor
    );
    sessionNewWorkouts.push(...distributedSessionWorkouts);
    const sessionFocusDomain = getSessionFocusDomain(sessionNewWorkouts, domainWeightsForAllWorkouts);

    // Add potential locked workouts
    yield call(
      addLockedWorkouts,
      cycleId,
      sessionIndex,
      sessionNewWorkouts,
      lockedWorkoutGroupIds,
      numberOfLockedWorkouts,
      categoryFilteredWorkouts
    );
    // Order workouts in session
    const orderedWorkouts = orderWorkoutsInSession(
      sessionNewWorkouts,
      performanceByWorkout,
      lockedWorkoutGroupIds,
      categoryFilteredWorkouts
    );

    currentSession = Utils.createSessionStatusClient(
      cycleId,
      sessionIndex,
      sessionFocusDomain,
      Types.ETrainingStatus.None,
      0,
      0
    );
    cycleNewSessions.push(currentSession);
    sessionIndex++;
    yield call(storeSession, currentSession, orderedWorkouts);
  }

  if (addSessions > 0) {
    // adding new session to cycle have to reset cycle finish timestamp.
    const startTimestamp = yield select(Selectors.getCycleStartTimestamp);
    yield put(CyclesActions.updateCycleStats(startTimestamp, 0));
  }
}

export function getUniqueSessionExerciseAmount(
  numberOfMissingWorkouts: number,
  repeatFactor: number,
  cycleWorkoutsToDistributeAmount: number
) {
  return Math.min(Math.max(Math.ceil(numberOfMissingWorkouts / repeatFactor), 1), cycleWorkoutsToDistributeAmount);
}

function findGroupDistribution(
  sessionGroupDistributedWorkouts: Map<number[], Types.IWorkoutDistribution[]>,
  sessionId: number
) {
  const sessionGroupDistributedWorkoutsEntries = Array.from(sessionGroupDistributedWorkouts);
  return (find(sessionGroupDistributedWorkoutsEntries, ([sessionIds, value]) => sessionIds.includes(sessionId)) ||
    [])[1];
}

interface IDomainSetting {
  domainId: EDomain;
  setting: number;
}

interface IAvailableWorkoutsForSessions {
  sessionIds: number[];
  workoutIds: number[];
}

export type TAvailableWorkoutsForSessions = IAvailableWorkoutsForSessions[];

const MIN_NON_PREMIUM = 3;

function sortWorkoutsByPopularity(
  popularityConfig: IWorkoutPopularityConfig,
  notExcludedWorkouts: ConfigTypes.IWorkoutConfig[]
) {
  const sortedWorkouts: ConfigTypes.IWorkoutConfig[] = [];
  const orderedWorkoutGroupIds = popularityConfig.workout_group_ids_by_popularity;
  for (const workoutGroupId of orderedWorkoutGroupIds) {
    const workout = notExcludedWorkouts.find((w) => w.categoryGroupId === workoutGroupId);
    if (!workout) {
      continue;
    }
    sortedWorkouts.push(workout);
    notExcludedWorkouts = notExcludedWorkouts.filter((w) => w.workoutId !== workout.workoutId);
  }
  // take rest of the workouts as Popularity
  sortedWorkouts.push(...notExcludedWorkouts);
  return sortedWorkouts;
}

function getReturnValues(
  sessionIdsToGenerate: number[],
  absoluteSessionIdsByWorkoutCount: Map<number, number[]>,
  absoluteSessionIdAdjustment: number
) {
  const sessionIdsByWorkoutCount: Map<number, number[]> = new Map();
  for (const sessionId of sessionIdsToGenerate) {
    const arr = Array.from(absoluteSessionIdsByWorkoutCount);
    let workoutCountEntry = find(arr, ([key, asIds]) => asIds.includes(sessionId + absoluteSessionIdAdjustment));
    if (!workoutCountEntry || !workoutCountEntry[1]) {
      workoutCountEntry = find(arr, ([key, asIds]) => asIds.includes(-1));
    }
    const workoutCount = workoutCountEntry ? workoutCountEntry[0] : 0;
    const existingSessionIds = sessionIdsByWorkoutCount.get(workoutCount) ?? [];
    const mergedIds = union(existingSessionIds, [sessionId]);
    sessionIdsByWorkoutCount.set(workoutCount, mergedIds);
  }
  return sessionIdsByWorkoutCount;
}

export function getPopularWorkoutsForSessions(
  sessionIdsToGenerate: number[],
  absoluteSessionIdAdjustment: number,
  categoryFilteredWorkouts: ConfigTypes.IWorkoutConfig[],
  excludedWorkoutsIds: number[],
  popularityConfig: IWorkoutPopularityConfig
) {
  const notExcludedWorkouts = categoryFilteredWorkouts.filter(
    (workoutConfig) => !excludedWorkoutsIds.includes(workoutConfig.workoutId)
  );

  // check if config is present and filter active
  if (
    !popularityConfig.popularity_filter_active ||
    popularityConfig.popularity_workouts_percentage_for_session.length === 0
  ) {
    const workoutIds = notExcludedWorkouts.map((workoutConfig) => workoutConfig.workoutId);
    const sessionIds = sessionIdsToGenerate;
    const availableWorkoutsForCycle: TAvailableWorkoutsForSessions = [
      {
        sessionIds,
        workoutIds,
      },
    ];
    return availableWorkoutsForCycle;
  }
  // sort workouts by Popularity
  const sortedWorkouts: ConfigTypes.IWorkoutConfig[] = sortWorkoutsByPopularity(popularityConfig, notExcludedWorkouts);

  // map session ids to workout lists
  const highestSessionInConfig = maxBy(
    popularityConfig.popularity_workouts_percentage_for_session,
    (item) => item.until_session
  );
  const absoluteSessionIdsByWorkoutCount: Map<number, number[]> = new Map();
  absoluteSessionIdsByWorkoutCount.set(sortedWorkouts.length, [-1]);
  let percentage: number;
  const untilSession = highestSessionInConfig ? highestSessionInConfig.until_session : 0;
  for (let i = 0; i < untilSession; i++) {
    percentage = 1;
    for (const sessionPercentage of popularityConfig.popularity_workouts_percentage_for_session) {
      if (i <= sessionPercentage.until_session) {
        percentage = Math.min(Math.max(sessionPercentage.available_percentage, 0), 1);
        const workoutCount = Math.min(
          Math.max(MIN_NON_PREMIUM, Math.ceil(categoryFilteredWorkouts.length * percentage)),
          sortedWorkouts.length
        );
        let existingIds = absoluteSessionIdsByWorkoutCount.get(workoutCount) ?? [];
        existingIds = union(existingIds, [i]);
        absoluteSessionIdsByWorkoutCount.set(workoutCount, existingIds);
        break;
      }
    }
  }
  const workoutIdsByWorkoutCount: { [count: number]: number[] } = {};
  const sortedWorkoutIds = map(sortedWorkouts, (w) => w.workoutId);
  for (const workoutCount of absoluteSessionIdsByWorkoutCount.keys()) {
    workoutIdsByWorkoutCount[workoutCount] = sortedWorkoutIds.slice(0, workoutCount);
  }
  // get return values
  const sessionIdsByWorkoutCount: Map<number, number[]> = getReturnValues(
    sessionIdsToGenerate,
    absoluteSessionIdsByWorkoutCount,
    absoluteSessionIdAdjustment
  );

  const availableWorkoutsForCycle: TAvailableWorkoutsForSessions = Array.from(sessionIdsByWorkoutCount).map(
    ([workoutCount, sessionIds]) => {
      return {
        workoutIds: workoutIdsByWorkoutCount[workoutCount],
        sessionIds,
      };
    }
  );

  return availableWorkoutsForCycle;
}

export function getTargetWorkoutDistribution(
  workoutId: number,
  lockedWorkoutGroupIds: number[],
  normalisedDomainSettings: IDomainSetting[],
  domainWeightsForAllWorkouts: Map<number, Map<number, number>>,
  workoutConfigurations: ConfigTypes.IWorkoutConfig[]
) {
  let targetDistribution = 0;
  const workoutConfig = find(workoutConfigurations, ['workoutId', workoutId]);
  if (!workoutConfig || !lockedWorkoutGroupIds.includes(workoutConfig.categoryGroupId)) {
    for (const domainSetting of normalisedDomainSettings) {
      const domainWeights = domainWeightsForAllWorkouts.get(domainSetting.domainId);
      const workoutWeight = domainWeights?.get(workoutId) ?? 0;
      targetDistribution += domainSetting.setting * workoutWeight;
    }
  }
  return targetDistribution;
}

export function distributeWorkouts(
  distribution: Types.IWorkoutDistribution[],
  amountToDistribute: number,
  repeatFactor = 1
) {
  const targetDistribution = orderBy(distribution, (d) => d.targetDistribution, 'desc');
  const distributedElements: Types.IWorkoutDistribution[] = [];
  let rests: Types.IWorkoutDistribution[] = [];
  let sumDistributed = 0;
  let amount = 0;
  targetDistribution.forEach((workoutDistribution) => {
    amount = Math.min(
      Math.max(Math.round(workoutDistribution.targetDistribution * amountToDistribute), repeatFactor),
      amountToDistribute - sumDistributed
    );
    distributedElements.push({
      workoutId: workoutDistribution.workoutId,
      targetDistribution: 0,
      amount,
    });
    rests.push({
      workoutId: workoutDistribution.workoutId,
      targetDistribution: amount - workoutDistribution.targetDistribution * amountToDistribute,
      amount: 0,
    });
    sumDistributed += amount;
  });
  rests = orderBy(rests, ['targetDistribution'], 'asc');
  let remainingDistributionLenght = amountToDistribute - sumDistributed;
  let restsIndex = 0;
  while (remainingDistributionLenght > 0) {
    if (restsIndex >= rests.length) {
      restsIndex = 0;
    }
    const restWorkoutId = rests[restsIndex]?.workoutId;
    const singleDistributionIndex = findIndex(distributedElements, (w) => w.workoutId === restWorkoutId);
    if (singleDistributionIndex >= 0) {
      const singleDistribution = distributedElements[singleDistributionIndex];
      singleDistribution.amount = (singleDistribution.amount || 0) + 1;
      distributedElements[singleDistributionIndex] = singleDistribution;
    }
    remainingDistributionLenght -= 1;
    restsIndex++;
  }

  return distributedElements;
}

export function getWorkoutsForSession(
  cycleId: number,
  sessionId: number,
  cycleDistributeWorkouts: Types.IWorkoutDistribution[] | undefined,
  sessionLength: number,
  uniqueWorkoutCount: number,
  workoutIndex: number,
  repeatFactor: number
): Types.IWorkoutClientStatus[] {
  cycleDistributeWorkouts = orderBy(cycleDistributeWorkouts || [], 'amount', 'desc');
  let selectedSum = 0;
  let amount = 0;
  while (amount < cycleDistributeWorkouts.length && (selectedSum < sessionLength || amount < uniqueWorkoutCount)) {
    selectedSum += Math.min(cycleDistributeWorkouts[amount].amount || 0, repeatFactor);
    amount++;
  }
  const targetSessionDistribution: Types.IWorkoutDistribution[] = [];
  for (let i = 0; i < amount; i++) {
    const distribution = cycleDistributeWorkouts[i];
    const targetDistribution = Math.min(distribution.amount || 0, repeatFactor) / selectedSum;
    targetSessionDistribution.push({
      workoutId: distribution.workoutId,
      targetDistribution,
      amount: 0,
    });
  }
  const distributedWorkouts = distributeWorkouts(targetSessionDistribution, sessionLength);
  const sessionWorkouts: Types.IWorkoutClientStatus[] = [];
  for (const distributedWorkout of distributedWorkouts) {
    const distributeWorkoutIndex = findIndex(cycleDistributeWorkouts, ['workoutId', distributedWorkout.workoutId]);
    if (distributeWorkoutIndex >= 0) {
      cycleDistributeWorkouts[distributeWorkoutIndex].amount -= distributedWorkout.amount;
    }
    for (let i = 0; i < distributedWorkout.amount; i++) {
      const tempSessionWorkoutVO: Types.IWorkoutClientStatus = {
        cycleId,
        sessionId,
        workoutId: distributedWorkout.workoutId,
        status: Types.ETrainingStatus.None,
        index: workoutIndex,
        finishedTimestamp: 0,
        syncTimestamp: 0,
      };
      sessionWorkouts.push(tempSessionWorkoutVO);
      workoutIndex++;
    }
  }

  return sessionWorkouts;
}

interface IWorkoutPerformanceStatus {
  workout: Types.IWorkoutClientStatus;
  performance: number;
}

export function orderWorkoutsInSession(
  sessionNewWorkouts: Types.IWorkoutClientStatus[],
  performanceByWorkout: Map<number, number>,
  lockedWorkoutGroupIds: number[],
  workoutConfigurations: ConfigTypes.IWorkoutConfig[]
) {
  const lockedWorkouts: Types.IWorkoutClientStatus[] = [];
  const availableWorkouts: Types.IWorkoutClientStatus[] = [];
  let finishedWorkouts: Types.IWorkoutClientStatus[] = [];
  sessionNewWorkouts.forEach((workout) => {
    if (
      workout.status === Types.ETrainingStatus.Finished ||
      workout.status === Types.ETrainingStatus.Ongoing ||
      workout.status === Types.ETrainingStatus.Skipped
    ) {
      finishedWorkouts.push(workout);
    } else {
      const workoutConfig = find(workoutConfigurations, ['workoutId', workout.workoutId]);
      if (lockedWorkoutGroupIds.includes(workoutConfig ? workoutConfig.categoryGroupId : -1)) {
        lockedWorkouts.push(workout);
      } else {
        availableWorkouts.push(workout);
      }
    }
  });
  // Generate indexes for available workouts
  let workoutWithPerformance: IWorkoutPerformanceStatus[] = [];
  availableWorkouts.forEach((workout) => {
    const workoutPerformance = performanceByWorkout.get(workout.workoutId);
    workoutWithPerformance.push({ workout, performance: workoutPerformance ?? 0 });
  });
  workoutWithPerformance = orderBy(workoutWithPerformance, 'performance', 'desc');
  const workoutWithPerformanceGrouped: IWorkoutPerformanceStatus[][] = [];
  workoutWithPerformance.forEach(({ workout, performance }) => {
    const lastGroup: IWorkoutPerformanceStatus[] | undefined =
      workoutWithPerformanceGrouped.length > 0
        ? workoutWithPerformanceGrouped[workoutWithPerformanceGrouped.length - 1]
        : undefined;
    if (
      lastGroup &&
      lastGroup.length > 0 &&
      lastGroup.length < 3 &&
      lastGroup[0].workout.workoutId === workout.workoutId
    ) {
      lastGroup.push({ workout, performance });
    } else {
      workoutWithPerformanceGrouped.push([{ workout, performance }]);
    }
  });
  let newWorkoutsOrder: Types.IWorkoutClientStatus[] = [];
  let startIndex = 0;
  let endIndex = workoutWithPerformance.length - 1;
  for (let i = 0; i < workoutWithPerformanceGrouped.length; i++) {
    const group = workoutWithPerformanceGrouped[i];
    for (const { workout } of group) {
      const newWorkout: Types.IWorkoutClientStatus = {
        ...workout,
      };
      if (i % 2 === 0) {
        newWorkout.index = startIndex;
        startIndex++;
      } else {
        newWorkout.index = endIndex;
        endIndex--;
      }
      newWorkoutsOrder.push(newWorkout);
    }
  }
  newWorkoutsOrder = sortBy(newWorkoutsOrder, 'index');

  // Now add locked workouts in between
  if (lockedWorkouts.length > 0) {
    const lockedWorkoutDistribution: Types.IWorkoutClientStatus[][] = [];
    for (let i = 0; i < newWorkoutsOrder.length - 1; i++) {
      lockedWorkoutDistribution.push([]);
    }
    if (lockedWorkoutDistribution.length === 0) {
      lockedWorkoutDistribution.push([]);
    }
    lockedWorkouts.forEach((workout, i) => {
      lockedWorkoutDistribution[i % lockedWorkoutDistribution.length].push(workout);
    });
    for (let i = lockedWorkoutDistribution.length - 1; i >= 0; i--) {
      lockedWorkoutDistribution[i].forEach((workout) => {
        // fix for when i+1 would be too high, needs to be checked in the future if this is still possible
        if (newWorkoutsOrder.length > i + 1) {
          newWorkoutsOrder.splice(i + 1, 0, workout);
        } else {
          newWorkoutsOrder.push(workout);
        }
      });
    }
  }
  // Now add already existing workouts in their original position
  finishedWorkouts = sortBy(finishedWorkouts, 'index');
  let lastFinishedIndex = 0;
  const orderedWorkouts: Types.IWorkoutClientStatus[] = [];
  finishedWorkouts.forEach((workout) => {
    orderedWorkouts.push({ ...workout });
    lastFinishedIndex = workout.index + 1;
  });
  // Fix indexes acording to the order
  for (let i = 0; i < newWorkoutsOrder.length; i++) {
    const workout = newWorkoutsOrder[i];
    orderedWorkouts.push({
      ...workout,
      index: lastFinishedIndex + i,
    });
  }
  return orderedWorkouts;
}

export function* checkLockedWorkouts(
  sessionNewWorkouts: Types.IWorkoutClientStatus[],
  lockedWorkoutGroupIds: number[],
  replaceableWorkoutCountById: Map<number, number>
) {
  let alreadyLockedWorkouts = 0;
  let replaceableWorkoutCountMax = 0;
  for (const workout of sessionNewWorkouts) {
    // get config for given workout directly from store as workout might be from other category (not in active list passed via parameters)
    // f.e. when LED changed the difficulty for existing workout
    const categoryGroupId = yield select(ConfigSelectors.getCategoryGroupIdForWorkout, workout.workoutId);
    if (lockedWorkoutGroupIds.includes(categoryGroupId)) {
      alreadyLockedWorkouts++;
    } else {
      //not already locked and not started can be potentially replaced
      const replaceable = find(Array.from(replaceableWorkoutCountById), (entry) => entry[0] === workout.workoutId);
      if (!replaceable) {
        const notStarted = filter(
          sessionNewWorkouts,
          (w) => w.workoutId === workout.workoutId && w.status === Types.ETrainingStatus.None
        );
        const count = notStarted.length;
        replaceableWorkoutCountById.set(workout.workoutId, count);
        replaceableWorkoutCountMax = Math.max(replaceableWorkoutCountMax, count);
      }
    }
  }
  return { alreadyLockedWorkouts, replaceableWorkoutCountMax };
}

export function* addLockedWorkouts(
  cycleId: number,
  sessionId: number,
  sessionNewWorkouts: Types.IWorkoutClientStatus[],
  lockedWorkoutGroupIds: number[],
  numberOfLockedWorkoutsToAdd: number,
  workoutConfigurations: ConfigTypes.IWorkoutConfig[]
) {
  // check how many locked workout is missing in session and check which one can be replaced by locked ines
  const replaceableWorkoutCountById: Map<number, number> = new Map();

  const {
    alreadyLockedWorkouts,
    replaceableWorkoutCountMax,
  }: { alreadyLockedWorkouts: number; replaceableWorkoutCountMax: number } = yield call(
    checkLockedWorkouts,
    sessionNewWorkouts,
    lockedWorkoutGroupIds,
    replaceableWorkoutCountById
  );

  numberOfLockedWorkoutsToAdd -= alreadyLockedWorkouts;

  // replace workouts with biggest occurance with random premium
  while (numberOfLockedWorkoutsToAdd > 0) {
    const workoutToReplace = find(
      Array.from(replaceableWorkoutCountById),
      (replaceable) => replaceable[1] === replaceableWorkoutCountMax
    );
    if (workoutToReplace && workoutToReplace[1] > 0) {
      const [workoutId, count] = workoutToReplace;
      replaceableWorkoutCountById.set(workoutId, count - 1);
      const workoutToReplaceIndex = findIndex(
        sessionNewWorkouts,
        (w) => w.workoutId === workoutId && w.status === Types.ETrainingStatus.None
      );
      if (~workoutToReplaceIndex) {
        const sessioWorkoutToReplace = sessionNewWorkouts[workoutToReplaceIndex];
        const rndIndex = Math.floor(Math.random() * lockedWorkoutGroupIds.length);
        const newWorkoutGroupId = lockedWorkoutGroupIds[rndIndex];
        const workoutConfig = find(workoutConfigurations, ['categoryGroupId', newWorkoutGroupId]);
        // if workout is disabled by user it might be not present in the list of configurations
        if (workoutConfig) {
          const newSessioWorkoutToReplace = Utils.createWorkoutStatusClient(
            cycleId,
            sessionId,
            workoutConfig.workoutId,
            sessioWorkoutToReplace.index,
            Types.ETrainingStatus.None,
            0,
            0
          );
          sessionNewWorkouts[workoutToReplaceIndex] = newSessioWorkoutToReplace;
        }
      }
    }
    numberOfLockedWorkoutsToAdd--;
  }
}

export function* storeSession(session: Types.ISessionClientStatus, workouts: Types.IWorkoutClientStatus[]) {
  const workoutsToUpdate: Types.IWorkoutClientStatus[] = yield call(updateWorkouts, workouts);
  // check if session has changed
  const sessionToUpdate: Types.ISessionClientStatus | undefined = yield call(updateSession, session, workoutsToUpdate);
  if (!sessionToUpdate) {
    // do nothing if nothing to update
    return;
  }
  yield call(saveSessionToDB, sessionToUpdate);
  yield put(CyclesActions.updateSession(sessionToUpdate));
  if (workoutsToUpdate.length > 0) {
    yield call(saveWorkoutsToDB, workoutsToUpdate);
    yield put(CyclesActions.updateWorkouts(workoutsToUpdate));
  }
}

export function* updateWorkouts(workouts: Types.IWorkoutClientStatus[]) {
  const updatedWorkouts: Types.IWorkoutClientStatus[] = [];
  for (const workout of workouts) {
    const existingWorkout: Types.IWorkoutClientStatus | undefined = yield select(
      Selectors.getSessionWorkoutByIndex,
      workout.cycleId,
      workout.sessionId,
      workout.index
    );
    if (!existingWorkout) {
      // if its a new workout we need to add it to session
      updatedWorkouts.push({
        ...workout,
        syncTimestamp: 0,
      });
      continue;
    }
    const timestampDiffers = workout.finishedTimestamp !== existingWorkout.finishedTimestamp;
    const statusDiffers = workout.status !== existingWorkout.status;
    const idDiffers = workout.workoutId !== existingWorkout.workoutId;
    if (timestampDiffers || statusDiffers || idDiffers) {
      // if some of important properties differs we need to update
      updatedWorkouts.push({
        ...workout,
        syncTimestamp: 0,
      });
    }
  }
  return updatedWorkouts;
}

export function* updateSession(session: Types.ISessionClientStatus, workoutsToUpdate: Types.IWorkoutClientStatus[]) {
  const existingSession: Types.ISessionClientStatus | undefined = yield select(
    Selectors.getCycleSession,
    session.cycleId,
    session.sessionId
  );
  if (!existingSession || workoutsToUpdate.length > 0) {
    // if its a new session or session has udpated workouts we need to save it
    return {
      ...session,
      syncTimestamp: 0,
    };
  }
  const timestampDiffers = session.finishedTimestamp !== existingSession.finishedTimestamp;
  const statusDiffers = session.status !== existingSession.status;
  const domainDiffers = session.domainId !== existingSession.domainId;
  if (timestampDiffers || statusDiffers || domainDiffers) {
    // if important property has changed we need to update session
    return {
      ...session,
      syncTimestamp: 0,
    };
  }
}

export function* replaceWorkoutSaga(action: PayloadAction<Types.IReplaceWorkoutPayload>) {
  const { sourceId, targetId } = action.payload;
  yield call(waitIfLoading);
  try {
    const cycleId = yield select(Selectors.getCurrentCycleId);
    const cycleSessions: Types.ISessionClientStatus[] = yield select(Selectors.getCycleSessions, cycleId);
    for (const session of cycleSessions) {
      if (Utils.isSessionFinished(session)) {
        // skip finished session
        continue;
      }
      const sessionId = session.sessionId;
      const sessionWorkouts: Types.IWorkoutClientStatus[] = yield select(
        Selectors.getSessionWorkouts,
        cycleId,
        sessionId
      );
      const updatedWorkouts: Types.IWorkoutClientStatus[] = [];
      for (const workout of sessionWorkouts) {
        if (!Utils.isWorkoutActive(workout)) {
          // ignore finished workouts
          continue;
        }
        if (workout.workoutId !== sourceId) {
          // ignore workouts with different workoutId
          continue;
        }
        // change workoutId
        const udpatedWorkout: Types.IWorkoutClientStatus = {
          ...workout,
          workoutId: targetId,
          syncTimestamp: 0,
        };
        updatedWorkouts.push(udpatedWorkout);
      }
      const updatedSession: Types.ISessionClientStatus = {
        ...session,
      };
      if (updatedWorkouts.length > 0) {
        // mark session for saving
        updatedSession.syncTimestamp = 0;
      }
      yield call(storeSession, updatedSession, updatedWorkouts);
    }
    // save everything to backend
    yield put(CyclesActions.saveCycle());
    // wait for success
    yield take(CyclesActions.cycleSaved);
    // inform about success
    yield put(CyclesActions.replaceWorkoutSuccess());
  } catch (error) {
    yield put(CyclesActions.replaceWorkoutFailed());
  }
}

export function* finishCurrentSessionSaga() {
  yield call(waitIfLoading);
  const isSessionFinished: boolean = yield select(Selectors.isCurrentSessionFinished);
  if (isSessionFinished) {
    yield put(CyclesActions.currentSessionFinished());
    return;
  }
  const cycleId = yield select(Selectors.getCurrentCycleId);
  const sessionId = yield select(Selectors.getCurrentSessionId);
  yield call(finishSessionSaga, cycleId, sessionId);
  // save everything to backend
  yield put(CyclesActions.saveCycle());
  yield take(CyclesActions.cycleSaved);
  yield put(CyclesActions.currentSessionFinished());
}

export function* finishSessionSaga(cycleId: number, sessionId: number) {
  // mark all active workouts as skipped
  const workouts: Types.IWorkoutClientStatus[] = yield select(Selectors.getSessionWorkouts, cycleId, sessionId);
  let wasUpdated = false;
  const updatedWorkouts: Types.IWorkoutClientStatus[] = workouts.map((workout) => {
    const isActive = Utils.isWorkoutActive(workout);
    if (!isActive) {
      // if workouts is already skipped or finished - ignore it
      return workout;
    }
    wasUpdated = true;
    return {
      ...workout,
      finishedTimestamp: -1,
      status: Types.ETrainingStatus.Skipped,
    };
  });
  // mark session as finished if needed
  const session: Types.ISessionClientStatus | undefined = yield select(Selectors.getCycleSession, cycleId, sessionId);
  if (!session) {
    // skip session if not found
    return;
  }
  const sessionUnfinished = session.status !== Types.ETrainingStatus.Finished;
  const sessionChanged = sessionUnfinished || wasUpdated;
  const updatedSession: Types.ISessionClientStatus = {
    ...session,
    finishedTimestamp: sessionUnfinished ? -1 : session.finishedTimestamp,
    status: Types.ETrainingStatus.Finished,
    syncTimestamp: sessionChanged ? 0 : session.syncTimestamp,
  };
  yield call(storeSession, updatedSession, updatedWorkouts);

  // if session status changed (was unfinished)
  // and not assessment
  const isAssessment = updatedSession.sessionId === ASSESSMENT_SESSION_ID;
  if (sessionUnfinished && !isAssessment) {
    yield call(increaseSessionCounter);
  }
  if (sessionChanged) {
    // invalidate training status if session status updated
    yield call(saveStateStatsToDB);
  }
}

export function* cleanCacheSaga() {
  yield call(CyclesDB.clear);
  yield put(CyclesActions.cleanDone());
}

export function* validateCurrentSession() {
  const isValid = yield call(isCurrentSessionValid);
  if (isValid) return;
  // recreate cycle
  yield call(logger, 'Starting recreating cycle...', EErrorLevel.Info);
  yield call(recreateSessions);
  yield put(CyclesActions.saveCycle());
  yield take(CyclesActions.cycleSaved);
}

export function* isCurrentSessionValid() {
  yield call(ConfigSaga.preloadConfig);
  yield call(preloadStatsSaga);
  const workouts: Types.IWorkoutClientStatus[] = yield select(Selectors.getCurrentSessionWorkouts);
  for (const workout of workouts) {
    if (Utils.isWorkoutCompleted(workout)) {
      // skip completed or skipped workouts
      continue;
    }
    // check if workout is supported on platform
    const supported = yield select(ConfigSelectors.isWorkoutReleasedForWeb, workout.workoutId);
    if (!supported) {
      yield call(logger, `Workout ${workout.workoutId} is not supported!`, EErrorLevel.Warning);
      return false;
    }
    // skip further validation for assessment and CFQ workouts - they don't have difficulty variations
    const isAssessmentWorkout = yield select(ConfigSelectors.isAssessmentWorkout, workout.workoutId);
    if (isAssessmentWorkout) {
      continue;
    }
    // skip further validation for challenge workouts
    const isChallengeWorkout = yield select(ConfigSelectors.isChallengeWorkout, workout.workoutId);
    if (isChallengeWorkout) {
      continue;
    }
    // check if workout has proper workouts id
    const categoryGroupId = yield select(ConfigSelectors.getCategoryGroupIdForWorkout, workout.workoutId);
    const categoryIdStat: EExerciseCategory | undefined = yield select(
      StatsSelectors.getStatIntValueById,
      makeExerciseCategoryStatString(categoryGroupId)
    );
    // if stat is not set use default category (normal difficulty)
    const categoryId = categoryIdStat ?? DEFAULT_CATEGORY_ID;
    const expectedWorkoutId = getWorkoutId(categoryId, categoryGroupId);
    if (workout.workoutId !== expectedWorkoutId) {
      yield call(
        logger,
        `Workout ${workout.workoutId} has incorrect category id, proper workout id must be ${expectedWorkoutId}!`,
        EErrorLevel.Warning
      );
      return false;
    }
  }
  return true;
}

export function* cloneSessionSaga(
  session: Types.ISessionClientStatus,
  incrementSessionId?: boolean,
  incrementCycleId?: boolean
) {
  const { cycleId, sessionId } = session;
  const updatedCycleId = incrementCycleId ? cycleId + 1 : cycleId;
  const updatedSessionId = incrementSessionId ? sessionId + 1 : sessionId;
  const workouts: Types.IWorkoutClientStatus[] = yield select(Selectors.getSessionWorkouts, cycleId, sessionId);
  const updatedStatus =
    sessionId === ASSESSMENT_SESSION_ID ? Types.ETrainingStatus.Skipped : Types.ETrainingStatus.None;
  const finishedTimestamp = updatedSessionId === ASSESSMENT_SESSION_ID ? -1 : 0;

  const updatedSession: Types.ISessionClientStatus = {
    ...session,
    cycleId: updatedCycleId,
    sessionId: updatedSessionId,
    finishedTimestamp,
    status: updatedStatus,
    syncTimestamp: 0,
  };
  const updatedWorkouts = workouts.map((workout) => ({
    ...workout,
    // prevent challenge workouts from being copied
    // copy normal alternative instead
    workoutId: Utils.stripChallenge(workout.workoutId),
    cycleId: updatedCycleId,
    sessionId: updatedSessionId,
    finishedTimestamp,
    status: updatedStatus,
    syncTimestamp: 0,
  }));

  yield call(storeSession, updatedSession, updatedWorkouts);
  const convertedSession = Utils.convertSessionStatusToApi(updatedSession, updatedWorkouts);
  return convertedSession;
}

export function* cloneLastCycleSaga() {
  const cycleId = yield select(Selectors.getCurrentCycleId);
  const cycle = yield select(Selectors.getCycle, cycleId);
  const nextCycleId = cycleId + 1;

  const sessions = yield select(Selectors.getCycleSessions, cycleId);
  const updatedSessions = yield all(sessions.map((session) => call(cloneSessionSaga, session, false, true)));

  const updatedCycle = {
    ...cycle,
    cycleId: nextCycleId,
    startTimestamp: -1,
    finishedTimestamp: 0,
    status: Types.ECycleStatus.None,
  };

  const convertedCycle = Utils.convertCycleStatusToApi(updatedCycle, updatedSessions);

  // finish current cycle if not finished
  yield put(CyclesActions.finishCycle());
  yield take(CyclesActions.cycleFinished);

  // save cloned cycle as new one
  yield call(createCycleSaga, convertedCycle);

  // check update state for current cycle
  yield call(detectCurrentCycleStatus);
  yield put(CyclesActions.cloneCycleFinished());
}

export function* cloneLastSessionSaga() {
  // clone current session
  const session: Types.ISessionClientStatus = yield select(Selectors.getCurrentSession);
  const updatedSession = {
    ...session,
    finishedTimestamp: -1,
    syncTimestamp: 0,
  };
  yield call(cloneSessionSaga, updatedSession, true);

  // we need to update cycle data to "unfinished" state and save it
  const startTimestamp = yield select(Selectors.getCycleStartTimestamp);
  // update finished timestamp
  yield put(CyclesActions.updateCycleStats(startTimestamp, 0));
  // update cycle status on client
  const cycleId = yield select(Selectors.getCurrentCycleId);
  const cycle = yield select(Selectors.getCycle, cycleId);
  const updatedCycle: Types.ICycleClientStatus = {
    ...cycle,
    finishedTimestamp: 0,
    syncTimestamp: 0,
  };
  yield put(CyclesActions.updateCycle(updatedCycle));
  // we also need to update training statistics on client
  const stats: Types.ITrainingDBStatus = yield select(Selectors.getCurrentCycleStats);
  const cycleLength = yield select(Selectors.getCycleLength);
  const updatedStats: Types.ITrainingDBStatus = {
    ...stats,
    currentCycleId: cycleId,
    currentCycleLength: cycleLength + 1, // added one session
    currentSessionId: updatedSession.sessionId + 1,
  };
  // udpate stats in state
  yield put(
    CyclesActions.updateStats(
      updatedStats.currentCycleId,
      updatedStats.currentSessionId,
      updatedStats.currentCycleLength,
      updatedStats.finishedCycles,
      updatedStats.finishedSessions,
      updatedStats.numberWorkouts,
      updatedStats.firstTimestamp,
      updatedStats.lastTimestamp
    )
  );
  yield call(saveTrainingStatsToClient, updatedStats);

  yield put(CyclesActions.saveCycle());
  yield take(CyclesActions.cycleSaved);

  // check update state for current cycle
  yield call(detectCurrentCycleStatus);
  yield put(CyclesActions.cloneLastSessionFinished());
}

export function* increaseSessionCounter() {
  const stats: Types.ITrainingDBStatus = yield select(Selectors.getCurrentCycleStats);
  const updatedStats: Types.ITrainingDBStatus = {
    ...stats,
    finishedSessions: stats.finishedSessions + 1,
  };
  // udpate stats in state
  yield put(
    CyclesActions.updateStats(
      updatedStats.currentCycleId,
      updatedStats.currentSessionId,
      updatedStats.currentCycleLength,
      updatedStats.finishedCycles,
      updatedStats.finishedSessions,
      updatedStats.numberWorkouts,
      updatedStats.firstTimestamp,
      updatedStats.lastTimestamp
    )
  );
}

export function* increaseWorkoutsCounter(timestamp?: number) {
  const stats: Types.ITrainingDBStatus = yield select(Selectors.getCurrentCycleStats);
  const updatedStats: Types.ITrainingDBStatus = {
    ...stats,
    numberWorkouts: stats.numberWorkouts + 1,
  };
  // udpate stats in state
  yield put(
    CyclesActions.updateStats(
      updatedStats.currentCycleId,
      updatedStats.currentSessionId,
      updatedStats.currentCycleLength,
      updatedStats.finishedCycles,
      updatedStats.finishedSessions,
      updatedStats.numberWorkouts,
      updatedStats.firstTimestamp,
      timestamp ?? updatedStats.lastTimestamp
    )
  );
}

export function* saveStateStatsToDB() {
  const stats: Types.ITrainingDBStatus = yield select(Selectors.getCurrentCycleStats);
  // save currently present stats to db
  // and enforce stats to be refetched from api as soon as possible
  // syncTimestamp=1 makes sure value will be reloaded at the next loading run
  yield call(saveTrainingStatsToClient, stats, 1);
}
